import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/api";
import LazyAvatar from "@/components/LazyAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ProfilePhotoCropper from "@/components/ProfilePhotoCropper";

// Read a picked file as a data URL so the cropper can render it.
async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Could not read the image"));
    r.readAsDataURL(file);
  });
}

export default function SpeakersAdmin() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", photo_url: "", affiliation: "", bio: "" };
  const [form, setForm] = useState(empty);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const load = async () => { const r = await api.get("/speakers"); setRows(r.data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (editing) await api.patch(`/speakers/${editing.id}`, form);
      else await api.post("/speakers", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const onUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      e.target.value = "";
      return;
    }
    try {
      const url = await fileToDataUrl(file);
      setCropSrc(url);
    } catch (err) {
      toast.error(err.message || "Could not read the image");
    } finally {
      e.target.value = "";
    }
  };
  return (
    <div>
      <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24] mb-3" data-testid="admin-new-speaker-btn"><Plus size={16} className="mr-1" /> New speaker</Button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((sp) => (
          <div key={sp.id} className="p-3 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm flex items-center gap-3" data-testid={`admin-speaker-row-${sp.id}`}>
            <LazyAvatar
              endpoint={`/speakers/${sp.id}/photo`}
              hasPhoto={sp.has_photo}
              name={sp.name}
              className="w-10 h-10 rounded-sm flex-shrink-0"
              round={false}
            />
            <div className="flex-1 min-w-0"><div className="font-medium truncate">{sp.name}</div><div className="text-xs text-[#7A828C] truncate">{sp.affiliation}</div></div>
            <button onClick={async () => {
              setEditing(sp);
              // Fetch the full photo data URL so the edit form can preview + replace it.
              let photo_url = "";
              if (sp.has_photo) {
                try { const r = await api.get(`/speakers/${sp.id}/photo`); photo_url = r.data?.photo_url || ""; } catch { /* ignore */ }
              }
              setForm({ name: sp.name, photo_url, affiliation: sp.affiliation || "", bio: sp.bio || "" });
              setOpen(true);
            }} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7]" data-testid={`edit-speaker-${sp.id}`}><PencilSimple size={16} /></button>
            <button onClick={async () => { if (!window.confirm("Delete speaker?")) return; await api.delete(`/speakers/${sp.id}`); load(); }} className="w-10 h-10 grid place-items-center rounded-sm hover:bg-[#F0F2F7] text-red-400" data-testid={`delete-speaker-${sp.id}`}><Trash size={16} /></button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E5E9F0]">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit speaker" : "New speaker"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="form-speaker-name" />
            <div>
              <Label>Photo</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onUploadPhoto}
                className="hidden"
                data-testid="form-speaker-photo-input"
              />
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {form.photo_url && (
                  <img src={form.photo_url} alt="preview" className="w-16 h-16 rounded-sm object-cover border border-[#E5E9F0]" data-testid="form-speaker-photo-preview" />
                )}
                <Button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]"
                  data-testid="form-speaker-photo-upload-btn"
                >
                  <UploadSimple size={14} className="mr-1.5" />
                  {form.photo_url ? "Replace photo" : "Upload photo"}
                </Button>
                {form.photo_url && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForm({ ...form, photo_url: "" })}
                    className="rounded-sm"
                    data-testid="form-speaker-photo-clear-btn"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-xs text-[#7A828C] mt-2">JPG, PNG, HEIC or WEBP. You&apos;ll be able to pan and zoom to frame it.</p>
              <details className="text-xs mt-2">
                <summary className="cursor-pointer text-[#7A828C] hover:text-[#000000]">…or paste a photo URL instead</summary>
                <Input
                  value={form.photo_url?.startsWith("data:") ? "" : form.photo_url}
                  onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                  placeholder="https://…"
                  className="rounded-sm mt-2 font-mono-time text-xs"
                  data-testid="form-speaker-photo-url"
                />
              </details>
            </div>
            <Input placeholder="Affiliation" value={form.affiliation} onChange={(e) => setForm({ ...form, affiliation: e.target.value })} className="rounded-sm" />
            <Textarea placeholder="Bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} className="rounded-sm" />
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#000000] text-[#FFFFFF] hover:bg-[#1c1c24]" data-testid="form-speaker-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ProfilePhotoCropper
        open={!!cropSrc}
        imageDataUrl={cropSrc}
        onApply={(url) => {
          setForm((f) => ({ ...f, photo_url: url }));
          setCropSrc(null);
          toast.success("Photo ready - click Save to keep it");
        }}
        onCancel={() => setCropSrc(null)}
      />
    </div>
  );
}
